import { useMemo, useState } from "react";

import { useTranslation } from "react-i18next";
import { Link } from "react-router";

import { useQuery } from "@tanstack/react-query";
import {
  CircleCheckIcon,
  CircleQuestionMarkIcon,
  EyeIcon,
  FileIcon,
  FolderIcon,
  InfoIcon,
  LightbulbIcon,
  MailOpenIcon,
  PlusIcon,
} from "lucide-react";

import { initUseQuery } from "simplydo/core/query";
import type { OpenAPI } from "simplydo/interfaces";
import { DownloadLink } from "simplydo/react/components/DownloadLink";
import { IconHeader } from "simplydo/react/components/IconHeader";
import { VideoPlayer } from "simplydo/react/components/VideoPlayer";
import { RichTextViewer } from "simplydo/react/richtext";
import { styled } from "simplydo/react/styled";
import { mobileOnly, tabletUp, useTheme } from "simplydo/react/theme";
import { Button, ButtonLink } from "simplydo/react/ui/button";
import { Card, CardContent } from "simplydo/react/ui/card";
import { Divider } from "simplydo/react/ui/divider";
import {
  Dropdown,
  DropdownItemLink,
  DropdownMenu,
} from "simplydo/react/ui/dropdown";
import { H3 } from "simplydo/react/ui/header";
import { Icon } from "simplydo/react/ui/icon";
import { Input } from "simplydo/react/ui/input";
import { Label } from "simplydo/react/ui/label";
import {
  Message,
  MessageContent,
  MessageHeader,
} from "simplydo/react/ui/message";
import { Popup } from "simplydo/react/ui/popup";

import api from "api";
import { useCurrentUser } from "utils/currentUser";
import util from "utils/utils";

import RedactedInfo from "components/lib/RedactedInfo";
import { FakeLink } from "components/lib/UI";

import type { ChallengeDetail, GenAIChallengeDraft } from "../types";
import ChallengeMyIdeas from "./Ideas/MyIdeas";
import ChallengeProblems from "./Problems";

const NewIdeaTrigger = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
`;

const ChallengeClosedActions = styled.div`
  margin-top: 10px;
  display: flex;
  flex-wrap: wrap;
  flex-direction: ${({ theme }) => (theme.sizes.isMobile ? "column" : "row")};
  .ui.button {
    ${mobileOnly} {
      &:not(:first-of-type) {
        margin-top: 10px;
      }
    }
    ${tabletUp} {
      &:not(:first-of-type) {
        margin-left: 10px;
      }
    }
  }
`;

const OverviewActionsContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: 25px;
  width: 100%;
`;

const redactedInfoWidths = [72, 81, 65, 78, 69, 84];

const OverviewActions = styled.div`
  display: flex;
  min-width: 66%;
  flex-direction: ${({ theme }) => (theme.sizes.isMobile ? "column" : "row")};
  align-items: ${({ theme }) =>
    theme.sizes.isMobile ? "center" : "flex-start"};
  justify-content: ${({ theme }) =>
    theme.sizes.isMobile ? "flex-start" : "space-evenly"};
  .ui.dropdown {
    height: 40px;
  }
  .ui.button,
  .ui.dropdown,
  ${NewIdeaTrigger} {
    flex-grow: 1;
    display: block;
    width: ${({ theme }) => (theme.sizes.isMobile ? "100%" : "auto")};
    &:not(:last-of-type) {
      margin-bottom: ${({ theme }) => (theme.sizes.isMobile ? "10px" : "0")};
      margin-right: ${({ theme }) => (theme.sizes.isMobile ? "0" : "5px")};
    }
  }
`;

type OverviewPreviewProps = { challenge: GenAIChallengeDraft; isPreview: true };

type OverviewLiveProps = {
  challenge: ChallengeDetail;
  isPreview?: false;
  newIdea: () => void;
  ipStatement?: OpenAPI.Schemas["IpStatement"] | null;
  ipStatementHandled: boolean;
  ipManagementInfo?: { hasSensitiveInfo: boolean; userHasSigned: boolean };
  setIpStatementAgreementOpen: (open: boolean) => void;
  redirectToLogin: () => void;
};

type OverviewProps = OverviewPreviewProps | OverviewLiveProps;

type ChallengeOverviewDisplay = GenAIChallengeDraft & {
  videoUrl?: string;
  ideaCount?: number;
};

const Overview = (props: OverviewProps) => {
  const isPreview = props.isPreview === true;
  const challengeDetail = isPreview ? undefined : props.challenge;
  const challenge = props.challenge as ChallengeOverviewDisplay;
  const newIdea = isPreview ? () => {} : props.newIdea;
  const ipStatement = isPreview ? null : props.ipStatement;
  const ipStatementHandled = isPreview ? true : props.ipStatementHandled;
  const ipManagementInfo = isPreview ? undefined : props.ipManagementInfo;
  const setIpStatementAgreementOpen = isPreview
    ? () => {}
    : props.setIpStatementAgreementOpen;
  const redirectToLogin = isPreview ? () => {} : props.redirectToLogin;
  const { t } = useTranslation();
  const [showMyIdeas, setShowMyIdeas] = useState(true);
  const theme = useTheme();
  const { video, videoUrl, reward, links, attachments, stage } = challenge;
  const {
    description = "",
    instructions,
    requirements,
    helpfulTips,
  } = challenge;
  const user = useCurrentUser();
  const canMakeIdea = challengeDetail
    ? util.canCreateIdea(user, challengeDetail)
    : false;

  const myIdeasQuery = useQuery(
    initUseQuery(
      api.challenges.getIdeas,
      [challengeDetail?._id ?? "", { scope: "me" }],
      { isEnabled: !!user?._id && !!challengeDetail?._id },
    ),
  );
  const myUnsubmittedIdeas = useMemo(
    () => (myIdeasQuery.data?.ideas ?? []).filter((idea) => !idea.isSubmitted),
    [myIdeasQuery.data?.ideas],
  );
  return (
    <div>
      {!isPreview && challengeDetail ? (
        <ChallengeProblems challenge={challengeDetail} />
      ) : null}
      {challenge.stage === "closed" ? (
        <Message color="blue">
          <MessageHeader>
            This {t("generic.challenge")} is currently closed to new{" "}
            {t("generic.ideas")}
          </MessageHeader>
          <ChallengeClosedActions>
            {(challenge.ideaVisibility === "users" &&
              challengeDetail &&
              util.canViewChallengeAsAudience(user, challengeDetail)) ||
            (challengeDetail &&
              util.canManageChallenge(user, challengeDetail)) ||
            (challengeDetail &&
              util.hasPermission(
                user,
                "challenge.viewIdeas",
                challengeDetail._id,
              )) ? (
              <MessageContent>
                You can still{" "}
                <Link to={`/challenges/${challenge._id}/ideas`}>
                  {challenge.votingVisibility === "users"
                    ? "view, like, and comment on"
                    : "view and comment on"}
                </Link>{" "}
                {t("generic.idea")} submissions.
                {challengeDetail &&
                util.canViewProjectBoard(user, challengeDetail) ? (
                  <span>
                    {" "}
                    You may also{" "}
                    <Link
                      to={`/challenges/${challenge._id}/ideas?queryView=board`}
                    >
                      view the project board
                    </Link>{" "}
                    to view and manage the progress of {t("generic.idea")}{" "}
                    implementations.
                  </span>
                ) : null}
              </MessageContent>
            ) : null}
          </ChallengeClosedActions>
        </Message>
      ) : null}
      {challengeDetail ? (
        <ChallengeMyIdeas
          challengeId={challengeDetail._id}
          showMyIdeas={showMyIdeas}
          setShowMyIdeas={setShowMyIdeas}
          style={{ marginBottom: 20 }}
        />
      ) : null}
      {(video || description) && (
        <H3
          className="challenge-tour-step-2"
          style={{ textTransform: "uppercase" }}
        >
          {t("challenge.details.overview")}
        </H3>
      )}
      {video && videoUrl && (
        <div>
          <VideoPlayer src={videoUrl} canExpand />
          <Divider hidden />
        </div>
      )}
      {description && (
        <div>
          <RichTextViewer value={description} />
          <Divider hidden />
        </div>
      )}

      {reward && (
        <div>
          <Card fluid>
            <CardContent>
              <IconHeader
                as="h3"
                icon={CircleCheckIcon}
                header={t("challenge.details.benefits")}
                content={<RichTextViewer value={reward} />}
                wrapped={theme.sizes.isMobile}
              />
            </CardContent>
          </Card>
          <Divider section />
        </div>
      )}

      {instructions && (
        <div>
          <H3 style={{ textTransform: "uppercase" }}>
            {t("challenge.details.instructions")}
          </H3>
          <RichTextViewer value={instructions} />
          <Divider section />
        </div>
      )}

      {requirements && (
        <div>
          <IconHeader
            as="h3"
            icon={MailOpenIcon}
            header={t("challenge.details.include")}
            content={<RichTextViewer value={requirements} />}
            wrapped={theme.sizes.isMobile}
          />
          <Divider section />
        </div>
      )}

      {helpfulTips && (
        <div>
          <IconHeader
            as="h3"
            icon={InfoIcon}
            header={t("challenge.details.tips")}
            content={<RichTextViewer value={helpfulTips} />}
            wrapped={theme.sizes.isMobile}
          />
          <Divider section />
        </div>
      )}

      {((links && links.length > 0) ||
        (attachments && attachments.length > 0)) && (
        <div>
          <IconHeader
            as="h3"
            icon={FolderIcon}
            header={t("challenge.links.title")}
            content={
              <div>
                {links?.map((link) => (
                  <p
                    key={link.link}
                    style={{ marginBottom: 10, wordBreak: "break-word" }}
                  >
                    <strong>{link.title}</strong>
                    <br />
                    <a
                      className="with-border"
                      href={link.link}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {link.link}
                    </a>
                  </p>
                ))}
                {attachments?.map((attachment) => (
                  <p
                    key={attachment.title}
                    style={{ marginBottom: 10, wordBreak: "break-word" }}
                  >
                    <strong>{attachment.title}</strong>
                    <br />
                    <DownloadLink
                      url={attachment.url ?? ""}
                      name={attachment.name ?? attachment.title ?? ""}
                    >
                      {attachment.name ?? attachment.title ?? ""}
                    </DownloadLink>
                  </p>
                ))}
              </div>
            }
          />
          <Divider hidden />
        </div>
      )}

      {ipManagementInfo?.hasSensitiveInfo &&
        util.organisationFeaturesEnabled(user, ["ipManagement"]) && (
          <Message color="blue">
            <H3>ADDITIONAL INFORMATION</H3>
            {!ipManagementInfo.userHasSigned ? (
              <>
                <p>
                  <Icon icon={InfoIcon} />{" "}
                  <strong>
                    This information will be available once you have agreed to
                    and signed the IP statement for this{" "}
                    {t("generic.challenge")}.
                  </strong>
                </p>
                <OverviewActionsContainer
                  style={{ alignItems: "flex-start", marginBottom: 0 }}
                >
                  {redactedInfoWidths.map((width, i) => (
                    <RedactedInfo key={i} width={width} />
                  ))}
                </OverviewActionsContainer>
              </>
            ) : (
              <>
                <p>
                  <strong>
                    <Icon icon={InfoIcon} /> You can read this information
                    because you have signed the IP statement for this{" "}
                    {t("generic.challenge")}.
                  </strong>
                </p>
                <RichTextViewer
                  value={ipStatement?.statementSensitiveInfo ?? ""}
                />
              </>
            )}
            <Button
              onClick={() => setIpStatementAgreementOpen(true)}
              style={{ marginTop: 10 }}
              color="secondary"
            >
              <Icon icon={FileIcon} />
              View IP statement
            </Button>
          </Message>
        )}

      {stage === "published" && !user && (
        <OverviewActionsContainer>
          <OverviewActions>
            {challenge.ideaVisibility === "users" && (
              <ButtonLink
                style={{
                  border: `1px solid ${theme.primaryColour}`,
                  color: theme.primaryColour,
                  backgroundColor: "#fff",
                  marginBottom: 5,
                }}
                to={`/challenges/${challenge._id}/ideas`}
              >
                <Icon icon={EyeIcon} />
                {t("challenge.viewOtherIdeas")}
              </ButtonLink>
            )}

            <NewIdeaTrigger>
              <Button
                onClick={canMakeIdea ? newIdea : () => redirectToLogin()}
                color="primary"
              >
                <Icon icon={PlusIcon} />
                {challenge.buttonText
                  ? challenge.buttonText
                  : t("challenge.ideas.createYourIdea")}
              </Button>
              <Popup
                trigger={
                  <div style={{ alignSelf: "center", marginTop: 3 }}>
                    <FakeLink withBorder>
                      <Icon icon={CircleQuestionMarkIcon} />
                      {t("challenge.details.ideaHelpTitle")}
                    </FakeLink>
                  </div>
                }
              >
                {t("challenge.details.ideaHelpDescription")}
              </Popup>
            </NewIdeaTrigger>

            {myUnsubmittedIdeas?.length === 1 && (
              <ButtonLink
                color="primary"
                to={`/ideas/${myUnsubmittedIdeas[0]?._id}`}
              >
                <Icon icon={LightbulbIcon} />
                {t("challenge.continueIdea")}
              </ButtonLink>
            )}

            {myUnsubmittedIdeas?.length > 1 && (
              <Dropdown
                trigger={
                  <Button color="primary">
                    <Icon icon={LightbulbIcon} />
                    {t("challenge.continueIdea")}
                  </Button>
                }
              >
                <DropdownMenu>
                  {myUnsubmittedIdeas.map((i) => (
                    <DropdownItemLink key={i._id} to={`/ideas/${i._id}`}>
                      {i.name}
                    </DropdownItemLink>
                  ))}
                </DropdownMenu>
              </Dropdown>
            )}
          </OverviewActions>
        </OverviewActionsContainer>
      )}

      {stage === "published" && user && (
        <OverviewActionsContainer>
          <OverviewActions>
            {(challenge.ideaCount !== undefined &&
              challenge.ideaCount >= 1 &&
              challenge.ideaVisibility === "users") ||
            (challengeDetail &&
              util.canManageChallenge(user, challengeDetail)) ||
            (challengeDetail &&
              util.hasPermission(
                user,
                "challenge.viewIdeas",
                challengeDetail._id,
              )) ? (
              <ButtonLink
                className="challenge-tour-step-3"
                color="primary"
                variant="outline"
                to={`/challenges/${challenge._id}/ideas`}
              >
                <Icon icon={EyeIcon} />
                {t("challenge.viewOtherIdeas")}
              </ButtonLink>
            ) : null}

            {canMakeIdea &&
            (!myUnsubmittedIdeas || myUnsubmittedIdeas?.length === 0) ? (
              <Button
                onClick={canMakeIdea ? newIdea : () => redirectToLogin()}
                disabled={!ipStatementHandled}
                className="challenge-tour-step-3"
                color="primary"
              >
                <Icon icon={PlusIcon} />
                {challenge.buttonText
                  ? challenge.buttonText
                  : t("challenge.ideas.createYourIdea")}
              </Button>
            ) : null}

            {myUnsubmittedIdeas?.length === 1 && (
              <ButtonLink
                color="primary"
                to={`/ideas/${myUnsubmittedIdeas[0]?._id}`}
              >
                <Icon icon={LightbulbIcon} />
                {t("challenge.continueIdea")}
              </ButtonLink>
            )}

            {myUnsubmittedIdeas?.length > 1 && (
              <Dropdown
                trigger={
                  <Button style={{ width: "100%" }} color="primary">
                    <Icon icon={LightbulbIcon} />
                    {t("challenge.continueIdea")}
                  </Button>
                }
              >
                <DropdownMenu>
                  {myUnsubmittedIdeas.map((i) => (
                    <DropdownItemLink key={i._id} to={`/ideas/${i._id}`}>
                      {i.name}
                    </DropdownItemLink>
                  ))}
                </DropdownMenu>
              </Dropdown>
            )}
          </OverviewActions>
          {(challengeDetail &&
            util.canManageChallenge(user, challengeDetail)) ||
          (challengeDetail &&
            util.hasPermission(
              user,
              "challenge.editSettings",
              challengeDetail._id,
            )) ? (
            <Input
              style={{ marginTop: 10 }}
              label={<Label>Login link</Label>}
              value={util.getBaseUrl(user?.ownerOrganisation)}
              readOnly
            />
          ) : null}
        </OverviewActionsContainer>
      )}
    </div>
  );
};

export default Overview;
